/**
 * The system execution order (TRD-01 §6, R-01-11).
 *
 * ⚠ THIS ORDER IS A VERSIONED API. ⚠
 *
 * Systems draw from a single shared RNG stream, so reordering them changes the sequence
 * of draws every subsequent system sees. That silently invalidates every stored replay
 * and every daily leaderboard entry — and it does so without any test failing unless one
 * is watching for exactly this. `order.test.ts` pins the list against a literal for that
 * reason.
 *
 * Adding a system is safe if it is appended or inserted with a matching SIM_VERSION bump
 * (TRD-10 R-10-17). Reordering existing systems is never safe.
 *
 * `tick++` is step 14 and is not a system — it happens after the pipeline, unconditionally.
 */
export const SYSTEM_ORDER = [
  /** 1. Player intent enters the world. Nothing else may mutate state from outside. */
  'applyCommands',
  /** 2. Only runs when the placed-tower set changed this tick (TRD-08 R-08-04). */
  'synergyRecompute',
  /** 3. Consume the wave's precomputed spawn schedule (TRD-06 R-06-07). */
  'waveDirector',
  /** 4. DoT ticks and expiries, before anything reads effective speed. */
  'statusTick',
  /** 5. Advance pathT, resolve x/y from the path spline. */
  'enemyMovement',
  /**
   * 5b. Disablers pick a tower and knock out everything around it (playtest request).
   *
   * BEFORE targeting and firing, so a tower disabled this tick does not also get a shot
   * off in the same one — the blast landing and the volley leaving on the same frame is
   * the kind of thing that makes a counter feel like it did not work.
   */
  'enemyAbilities',
  /** 6. Acquire and validate targets — after movement, so ranges are current. */
  'towerTargeting',
  /** 7. Cooldowns, projectile spawns, on-fire hooks. */
  'towerFiring',
  /** 8. Integrate, collide, resolve damage, chain and pierce. */
  'projectileMovement',
  /** 9. Swarm drones. */
  'summonTick',
  /** 10. On-death effects, splitters, gold. Deferred from step 8 so kills never mutate
   *      an array that step 8 is still iterating (TRD-05 R-05-20). */
  'deathResolution',
  /** 11. Enemies that reached the Core. */
  'leakResolution',
  /** 12. Payout and phase transition. */
  'waveCompletion',
  /** 13. Sweep tombstones. Always last, so every system above sees a stable array. */
  'compaction',
] as const;

export type SystemName = (typeof SYSTEM_ORDER)[number];
